/**
 * Demo data for premium features preview (web)
 * Показывает пользователям как будут выглядеть премиум функции
 */

import { useAppStore } from '@alveon/shared/store';

type DemoRegion = 'RU' | 'US' | 'EU_EN' | 'EU_FR' | 'EU_DE' | 'EU_ES' | 'TR' | 'JP' | 'KR';

const timezoneToCountry: Record<string, string> = {
  'Europe/Moscow': 'RU', 'Europe/Samara': 'RU', 'Europe/Volgograd': 'RU',
  'Europe/Kaliningrad': 'RU', 'Asia/Yekaterinburg': 'RU', 'Asia/Novosibirsk': 'RU',
  'Asia/Krasnoyarsk': 'RU', 'Asia/Irkutsk': 'RU', 'Asia/Vladivostok': 'RU',
  'Asia/Kamchatka': 'RU', 'Asia/Magadan': 'RU', 'Asia/Sakhalin': 'RU',
  'Europe/Minsk': 'BY', 'Europe/Kiev': 'UA', 'Europe/Kyiv': 'UA',
  'Asia/Almaty': 'KZ', 'Asia/Tashkent': 'UZ', 'Asia/Bishkek': 'KG',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA',
  'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT', 'Europe/London': 'GB', 'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH', 'Europe/Istanbul': 'TR',
  'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR',
};

const countryToDemoRegion: Record<string, DemoRegion> = {
  RU: 'RU', BY: 'RU', KZ: 'RU', UA: 'RU', UZ: 'RU',
  US: 'US', CA: 'US',
  FR: 'EU_FR', DE: 'EU_DE', AT: 'EU_DE', CH: 'EU_DE',
  ES: 'EU_ES', TR: 'TR', JP: 'JP', KR: 'KR',
  GB: 'EU_EN', IT: 'EU_EN', IE: 'EU_EN',
};

function webTimezoneToRegion(tz: string): string {
  if (!tz) return 'US';
  const exact = timezoneToCountry[tz];
  if (exact) return exact;
  if (tz.startsWith('Europe/')) return 'EU';
  if (tz.startsWith('America/')) return 'US';
  if (tz.startsWith('Asia/')) return 'ASIA';
  return 'US';
}

function getDemoRegion(): DemoRegion {
  try {
    const settings = useAppStore.getState().settings;

    const tz = settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      const country = webTimezoneToRegion(tz);
      if (country && countryToDemoRegion[country]) return countryToDemoRegion[country];
      if (country === 'EU') return 'EU_EN';
    }

    const currency = settings?.currency_code || settings?.currency || 'USD';
    if (['RUB', 'BYN', 'KZT', 'UAH', 'UZS'].includes(currency)) return 'RU';
    if (['USD', 'CAD'].includes(currency)) return 'US';
    if (['TRY'].includes(currency)) return 'TR';
    if (['JPY'].includes(currency)) return 'JP';
    if (['KRW'].includes(currency)) return 'KR';
    if (['EUR', 'GBP', 'CHF'].includes(currency)) return 'EU_EN';

    return 'US';
  } catch {
    return 'US';
  }
}

function getCurrencySymbol(region: DemoRegion): string {
  switch (region) {
    case 'RU': return '₽';
    case 'EU_EN': case 'EU_FR': case 'EU_DE': case 'EU_ES': return '€';
    case 'TR': return '₺';
    case 'JP': return '¥';
    case 'KR': return '₩';
    case 'US': default: return '$';
  }
}

const VOICE_DEMO: Record<DemoRegion, { text: string; parsed: { title: string; amount: number } }> = {
  RU:    { text: 'Купил кофе в кофейне за 450 рублей', parsed: { title: 'Кофе в кофейне', amount: 450 } },
  US:    { text: 'Bought coffee at Starbucks for 6 dollars', parsed: { title: 'Coffee at Starbucks', amount: 6 } },
  EU_EN: { text: 'Bought coffee at Costa for 5 euros', parsed: { title: 'Coffee at Costa', amount: 5 } },
  EU_FR: { text: 'Acheté un café chez Paul pour 5 euros', parsed: { title: 'Café chez Paul', amount: 5 } },
  EU_DE: { text: 'Kaffee bei Tchibo gekauft für 5 Euro', parsed: { title: 'Kaffee bei Tchibo', amount: 5 } },
  EU_ES: { text: 'Compré café en Costa por 5 euros', parsed: { title: 'Café en Costa', amount: 5 } },
  TR:    { text: 'Kahve aldım 75 lira', parsed: { title: 'Kahve', amount: 75 } },
  JP:    { text: 'スターバックスでコーヒー550円', parsed: { title: 'スターバックス コーヒー', amount: 550 } },
  KR:    { text: '스타벅스 커피 5500원', parsed: { title: '스타벅스 커피', amount: 5500 } },
};

const RECEIPT_DEMO: Record<DemoRegion, { store: string; date: string; items: Array<{ name: string; price: number; quantity: number }>; total: number }> = {
  RU: { store: 'Пятёрочка', date: new Date().toLocaleDateString('ru-RU'), items: [
    { name: 'Молоко 3.2% 1л', price: 89, quantity: 2 }, { name: 'Хлеб белый', price: 45, quantity: 1 },
    { name: 'Сыр 200г', price: 189, quantity: 1 }], total: 412 },
  US: { store: 'Whole Foods', date: new Date().toLocaleDateString('en-US'), items: [
    { name: 'Organic Milk 1gal', price: 6.99, quantity: 1 }, { name: 'Whole Wheat Bread', price: 4.49, quantity: 1 }], total: 11.48 },
  EU_EN: { store: 'Tesco', date: new Date().toLocaleDateString('en-GB'), items: [
    { name: 'Whole Milk 1L', price: 1.29, quantity: 2 }, { name: 'White Bread', price: 1.49, quantity: 1 }], total: 4.07 },
  EU_FR: { store: 'Carrefour', date: new Date().toLocaleDateString('fr-FR'), items: [
    { name: 'Lait Entier 1L', price: 1.19, quantity: 2 }, { name: 'Baguette', price: 1.30, quantity: 1 }], total: 3.68 },
  EU_DE: { store: 'REWE', date: new Date().toLocaleDateString('de-DE'), items: [
    { name: 'Vollmilch 1L', price: 1.19, quantity: 2 }, { name: 'Vollkornbrot', price: 1.89, quantity: 1 }], total: 4.27 },
  EU_ES: { store: 'Mercadona', date: new Date().toLocaleDateString('es-ES'), items: [
    { name: 'Leche Entera 1L', price: 1.10, quantity: 2 }, { name: 'Pan de Molde', price: 1.35, quantity: 1 }], total: 3.55 },
  TR: { store: 'Migros', date: new Date().toLocaleDateString('tr-TR'), items: [
    { name: 'Süt 1L', price: 45, quantity: 2 }, { name: 'Ekmek', price: 15, quantity: 1 }], total: 105 },
  JP: { store: 'ローソン', date: new Date().toLocaleDateString('ja-JP'), items: [
    { name: '牛乳 1L', price: 198, quantity: 2 }, { name: '食パン', price: 168, quantity: 1 }], total: 564 },
  KR: { store: 'GS25', date: new Date().toLocaleDateString('ko-KR'), items: [
    { name: '우유 1L', price: 2800, quantity: 2 }, { name: '식빵', price: 3500, quantity: 1 }], total: 9100 },
};

export function getDemoVoiceInput() {
  const region = getDemoRegion();
  return {
    ...VOICE_DEMO[region],
    currencySymbol: getCurrencySymbol(region),
  };
}

export function getDemoReceiptScan() {
  const region = getDemoRegion();
  return {
    ...RECEIPT_DEMO[region],
    currencySymbol: getCurrencySymbol(region),
  };
}
